import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { distinctUntilChanged, pairwise, startWith } from 'rxjs/operators';
import {
  getPreventiveTreatmentCategoryLabelKey,
  getPreventiveTreatmentTypeLabelKey,
  PreventiveTreatmentCategory,
  PreventiveTreatmentType
} from '../../../models/medical/medical.enums';
import { shortestIntervalMonths, suggestNextDue, typesForCategoryAndSpecies } from '../../../models/medical/medical.helpers';
import { PreventiveTreatmentTypeDefinition } from '../../../models/medical/preventive-treatment-types.const';
import { TreatmentForm } from '../medical-record-form';

/** Eine Behandlung im Karteikarten-Modal (Impfung, Endo-/Ektoparasiten, Medikation). */
@Component({
  selector: 'app-medical-treatment-fields',
  templateUrl: './medical-treatment-fields.component.html',
  standalone: false
})
export class MedicalTreatmentFieldsComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) group!: TreatmentForm;
  @Input() animalCategory: string | null = null;
  /** Termindatum: Basis für den Fälligkeitsvorschlag. */
  @Input() recordDate: Date | null = null;
  @Input() readonly = false;
  @Output() remove = new EventEmitter<void>();

  readonly categoryOptions = Object.values(PreventiveTreatmentCategory).map(value => ({
    value,
    label: getPreventiveTreatmentCategoryLabelKey(value),
  }));
  protected readonly typeLabelKey = getPreventiveTreatmentTypeLabelKey;

  typeOptions: PreventiveTreatmentTypeDefinition[] = [];

  private readonly _destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.updateTypeOptions();

    this.group.controls.category.valueChanges
      .pipe(startWith(this.group.controls.category.value), distinctUntilChanged(), pairwise(), takeUntil(this._destroy$))
      .subscribe(() => {
        // Beim Kategoriewechsel passen die Erreger nicht mehr.
        this.group.controls.types.setValue([]);
        this.updateTypeOptions();
      });

    this.group.controls.types.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => this.applySuggestion());

    this.group.controls.noFurtherDue.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(noFurtherDue => {
      if (noFurtherDue) {
        this.group.controls.nextDueAt.disable({ emitEvent: false });
      } else {
        this.group.controls.nextDueAt.enable({ emitEvent: false });
        this.applySuggestion();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['recordDate'] && !changes['recordDate'].firstChange) {
      this.applySuggestion();
    }
    if (changes['animalCategory'] && !changes['animalCategory'].firstChange) {
      this.updateTypeOptions();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get isMedication(): boolean {
    return this.group.controls.category.value === PreventiveTreatmentCategory.MEDICATION;
  }

  get selectedTypes(): PreventiveTreatmentType[] {
    return this.group.controls.types.value ?? [];
  }

  get intervalMonths(): number | null {
    return shortestIntervalMonths(this.selectedTypes);
  }

  /** Tollwut: Charge ist rechtlich relevant (Hinweis, nicht blockierend). */
  get batchNumberRecommended(): boolean {
    return this.selectedTypes.includes(PreventiveTreatmentType.RABIES) && !this.group.controls.batchNumber.value?.trim();
  }

  isTypeSelected(type: PreventiveTreatmentType): boolean {
    return this.selectedTypes.includes(type);
  }

  toggleType(type: PreventiveTreatmentType): void {
    if (this.readonly) return;
    const types = this.isTypeSelected(type)
      ? this.selectedTypes.filter(selected => selected !== type)
      : [...this.selectedTypes, type];
    this.group.controls.types.setValue(types);
    this.group.markAsDirty();
  }

  markNextDueManual(): void {
    this.group.controls.nextDueManual.setValue(true);
  }

  private updateTypeOptions(): void {
    this.typeOptions = this.isMedication ? [] : typesForCategoryAndSpecies(this.group.controls.category.value, this.animalCategory);
  }

  /** Vorschlag nur, solange die Fälligkeit nicht von Hand gesetzt oder abgewählt ist. */
  private applySuggestion(): void {
    const controls = this.group.controls;
    if (controls.nextDueManual.value || controls.noFurtherDue.value) return;
    controls.nextDueAt.setValue(suggestNextDue(this.recordDate, this.selectedTypes), { emitEvent: false });
  }
}
